import { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { prepareMapForLayerToggles } from './utils/mapLayerPromotion';
import { transformStyleUrls, MAP_MAX_ZOOM } from './components/map/config/mapConfig';
import { clearMapboxTileCacheIfPromoted, fetchPromotedTileVersions } from './lib/tileVersions';
import MapSplashLoader from './components/ui/MapSplashLoader';
import { MAPBOX_TOKEN } from './lib/env';
import {
    attachCoordalCatalogIcons,
    iconThemeForMapStyle,
} from './components/map/icons/attachCoordalCatalogIcons';
const GHANA_STYLE_URL = '/coordal-standard-style.json';

// Ghana bounding box (approximate)
const GHANA_BOUNDS: [[number, number], [number, number]] = [
    [-3.5, 4.5],   // Southwest: [lng, lat]
    [1.5, 11.5]    // Northeast: [lng, lat]
];

// Default location: University of Ghana, Legon
const DEFAULT_LOCATION = {
    lat: 5.6508,
    lng: -0.1869,
    name: 'University of Ghana'
};

// Popular Ghana locations for fallback (exported for potential future use)
export const GHANA_LOCATIONS = [
    { lat: 5.6508, lng: -0.1869, name: 'University of Ghana' },
    { lat: 5.5560, lng: -0.1969, name: 'Accra' },
    { lat: 6.6885, lng: -1.6244, name: 'Kumasi' },
    { lat: 5.1315, lng: -1.2795, name: 'Cape Coast' },
    { lat: 5.5500, lng: -0.2167, name: 'Tema' },
] as const;

// transformStyleUrls is imported from mapConfig — handles tile URLs, sprite URLs, and schema flattening

// Check if coordinates are within Ghana bounds
const isInGhana = (lat: number, lng: number): boolean => {
    return lng >= GHANA_BOUNDS[0][0] && lng <= GHANA_BOUNDS[1][0] &&
        lat >= GHANA_BOUNDS[0][1] && lat <= GHANA_BOUNDS[1][1];
};

// Parse URL parameters
const getEmbedParams = () => {
    const params = new URLSearchParams(window.location.search);
    const hash = window.location.hash;

    // Parse hash format: #zoom/lat/lng
    let hashLat: number | null = null;
    let hashLng: number | null = null;
    let hashZoom: number | null = null;

    if (hash) {
        const match = hash.match(/#(\d+(?:\.\d+)?)\/([-\d.]+)\/([-\d.]+)/);
        if (match) {
            hashZoom = parseFloat(match[1]);
            hashLat = parseFloat(match[2]);
            hashLng = parseFloat(match[3]);
        }
    }

    // Query params take precedence over hash
    const lat = params.get('lat') ? parseFloat(params.get('lat')!) : hashLat;
    const lng = params.get('lng') ? parseFloat(params.get('lng')!) : hashLng;
    const zoom = params.get('zoom') ? parseFloat(params.get('zoom')!) : hashZoom;
    const style = params.get('style') || 'standard';
    const animate = params.get('animate') !== 'false'; // Default true
    const startZoom = params.get('startZoom') ? parseFloat(params.get('startZoom')!) : 14;
    const endZoom = params.get('endZoom') ? parseFloat(params.get('endZoom')!) : 18;
    const marker = params.get('marker') === 'true';
    const markerColor = params.get('markerColor') || '#06b6d4';

    const cinematic = params.get('cinematic') === 'true';
    const pulse = params.get('pulse') === 'true';

    return { lat, lng, zoom, style, animate, startZoom, endZoom, marker, markerColor, cinematic, pulse };
};

export default function EmbedApp() {
    const mapContainer = useRef<HTMLDivElement>(null);
    const map = useRef<mapboxgl.Map | null>(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [ghanaStyleJson, setGhanaStyleJson] = useState<any>(null);
    const [showAttribution, setShowAttribution] = useState(false);
    const hasAnimated = useRef(false);
    const showPulse = useRef(getEmbedParams().pulse);
    const detachCatalogIconsRef = useRef<(() => void) | null>(null);

    // Fetch Ghana style
    useEffect(() => {
        fetch(`${GHANA_STYLE_URL}?t=${Date.now()}`)
            .then(res => res.json())
            .then(async style => {
                const { layers: layerVersions, cacheBust } = await fetchPromotedTileVersions();
                await clearMapboxTileCacheIfPromoted(cacheBust);
                setGhanaStyleJson(transformStyleUrls(style, layerVersions));
            })
            .catch(err => console.error('Failed to load Ghana style:', err));
    }, []);

    // Initialize map
    useEffect(() => {
        if (!mapContainer.current || map.current || !ghanaStyleJson) return;

        const params = getEmbedParams();

        // Determine initial location
        let initialLat = params.lat ?? DEFAULT_LOCATION.lat;
        let initialLng = params.lng ?? DEFAULT_LOCATION.lng;

        // If coordinates are outside Ghana, default to University of Ghana
        if (!isInGhana(initialLat, initialLng)) {
            initialLat = DEFAULT_LOCATION.lat;
            initialLng = DEFAULT_LOCATION.lng;
        }

        const initialZoom = params.cinematic ? 6 : params.animate ? params.startZoom : (params.zoom ?? 16);
        const initialPitch = params.cinematic ? 0 : 45;
        const initialBearing = params.cinematic ? 0 : -17.6;

        mapboxgl.accessToken = MAPBOX_TOKEN;

        const mapOptions: mapboxgl.MapOptions = {
            container: mapContainer.current,
            style: ghanaStyleJson,
            center: [initialLng, initialLat],
            zoom: initialZoom,
            pitch: initialPitch,
            bearing: initialBearing,
            projection: 'globe' as any,
            attributionControl: false,
            interactive: !params.cinematic,
            fadeDuration: 0,
            localIdeographFontFamily: 'sans-serif',
            maxZoom: MAP_MAX_ZOOM,
        };

        // Only restrict bounds in non-cinematic mode (cinematic needs full zoom range)
        if (!params.cinematic) {
            mapOptions.maxBounds = [
                [-4.5, 3.5],  // Southwest with padding
                [2.5, 12.5]   // Northeast with padding
            ] as any;
        }

        map.current = new mapboxgl.Map(mapOptions);

        const MAP_LOAD_FALLBACK_MS = 22_000;
        let loadFallbackTimer: number | undefined = window.setTimeout(() => {
            loadFallbackTimer = undefined;
            if (!map.current) return;
            setIsLoaded(true);
        }, MAP_LOAD_FALLBACK_MS) as unknown as number;
        const clearLoadFallback = () => {
            if (loadFallbackTimer !== undefined) {
                window.clearTimeout(loadFallbackTimer);
                loadFallbackTimer = undefined;
            }
        };

        // Add navigation controls (minimal) — hide in cinematic mode
        if (!params.cinematic) {
            map.current.addControl(
                new mapboxgl.NavigationControl({ showCompass: true, showZoom: true }),
                'bottom-right'
            );
        }

        // Custom attribution is rendered as an overlay (see JSX below)
        // Default Mapbox attribution is disabled via attributionControl: false

        map.current.on('load', () => {
            clearLoadFallback();
            setIsLoaded(true);

            detachCatalogIconsRef.current?.();
            const embedTheme = iconThemeForMapStyle(
                getEmbedParams().style === 'dark' ? 'dark' : 'standard',
            );
            if (map.current) {
                detachCatalogIconsRef.current = attachCoordalCatalogIcons(
                    map.current,
                    () => map.current,
                    embedTheme,
                );
            }

            // Mapbox GL v3 moves style layers into an internal import scope,
            // making them inaccessible via getLayer()/setLayoutProperty().
            // Promote all sources and layers from imports back to root level.
            if (map.current) {
                prepareMapForLayerToggles(map.current);
            }

            // Add marker if requested
            if (params.marker && map.current) {
                new mapboxgl.Marker({ color: params.markerColor })
                    .setLngLat([initialLng, initialLat])
                    .addTo(map.current);
            }

            // Cinematic loop: country → building with 3D → terrain on pullback → reset → repeat
            if (params.cinematic && map.current) {
                const m = map.current;
                let cancelled = false;

                // Helper to safely toggle layer visibility
                const setLayerVisible = (id: string, visible: boolean) => {
                    try { m.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none'); } catch (_) { }
                };

                // Helper to set basemap config property
                const setConfig = (prop: string, value: boolean | number | string) => {
                    try { (m as any).setConfigProperty('basemap', prop, value); } catch (_) { }
                };

                // Helper to toggle 3D buildings (must also set basemap config)
                const set3DBuildings = (on: boolean) => {
                    setLayerVisible('3d-building', on);
                    setLayerVisible('2d-building', !on);
                    setLayerVisible('2d-building-outline-ghana', !on);
                    setConfig('show3dObjects', true);
                };

                // Helper to toggle terrain (DEM + hillshade + contours)
                const setTerrain = (on: boolean, exaggeration = 1.5) => {
                    try {
                        if (on && m.getSource('ghana-terrain-rgb')) {
                            m.setTerrain({ source: 'ghana-terrain-rgb', exaggeration });
                        } else {
                            m.setTerrain(null as any);
                        }
                    } catch (_) { }
                    setLayerVisible('ghana-hillshade-shadow', on);
                    setLayerVisible('ghana-hillshade-highlight', on);
                    setLayerVisible('ghana-contours-minor', on);
                    setLayerVisible('ghana-contours-minor-label', on);
                    setLayerVisible('ghana-contours-major', on);
                    setLayerVisible('ghana-contours-label', on);
                };

                // Accra coordinates for the building-level destination
                const ACCRA: [number, number] = [-0.1969, 5.5560];
                // Eastern Region — Kwahu Plateau / Afadjato area for terrain showcase
                const EASTERN_REGION: [number, number] = [-0.45, 6.85];
                // Ghana center for the country-level view
                const GHANA_CENTER: [number, number] = [initialLng, initialLat];

                const runCinematic = () => {
                    if (cancelled || !m) return;

                    // Reset: ensure 3D buildings off, terrain off, back at country center
                    set3DBuildings(false);
                    setTerrain(false);

                    // Phase 1: Fly from country center down to Accra at building level (14s)
                    m.flyTo({
                        center: ACCRA,
                        zoom: 18,
                        pitch: 60,
                        bearing: -30,
                        duration: 14000,
                        essential: true,
                    });

                    // Phase 1b: Enable 3D buildings as we approach building zoom (~11s in)
                    setTimeout(() => {
                        if (cancelled) return;
                        set3DBuildings(true);
                    }, 11000);

                    // Phase 2: Orbit at building level with 3D (6s) — t=15s
                    setTimeout(() => {
                        if (cancelled) return;
                        m.easeTo({
                            bearing: 30,
                            pitch: 65,
                            duration: 6000,
                            easing: (t: number) => t,
                        });
                    }, 15000);

                    // Phase 3: Steep tilt to showcase 3D buildings from above (4s) — t=22s
                    setTimeout(() => {
                        if (cancelled) return;
                        m.easeTo({
                            bearing: 60,
                            pitch: 72,
                            zoom: 17.5,
                            duration: 4000,
                            easing: (t: number) => t * (2 - t),
                        });
                    }, 22000);

                    // Phase 4: Turn off 3D buildings, enable terrain (9x), fly to Eastern Region at zoom 8.6 (10s) — t=27s
                    setTimeout(() => {
                        if (cancelled) return;
                        set3DBuildings(false);
                        setTerrain(true, 9);
                        m.flyTo({
                            center: EASTERN_REGION,
                            zoom: 8.6,
                            pitch: 55,
                            bearing: -20,
                            duration: 10000,
                            essential: true,
                        });
                    }, 27000);

                    // Phase 5a: Tilt steeply + zoom to 10 to showcase terrain relief (6s) — t=38s
                    setTimeout(() => {
                        if (cancelled) return;
                        m.easeTo({
                            zoom: 10,
                            pitch: 70,
                            bearing: 10,
                            duration: 6000,
                            easing: (t: number) => t * (2 - t),
                        });
                    }, 38000);

                    // Phase 5b: Slow orbit to pan across the terrain (5s) — t=45s
                    setTimeout(() => {
                        if (cancelled) return;
                        m.easeTo({
                            bearing: 40,
                            pitch: 72,
                            duration: 5000,
                            easing: (t: number) => t,
                        });
                    }, 45000);

                    // Phase 6: Fly back to country center, flatten, terrain off (10s) — t=51s
                    setTimeout(() => {
                        if (cancelled) return;
                        setTerrain(false);
                        m.flyTo({
                            center: GHANA_CENTER,
                            zoom: 6,
                            pitch: 0,
                            bearing: 0,
                            duration: 10000,
                            essential: true,
                        });
                    }, 51000);

                    // Phase 7: Pause at country level, then restart — t=63s
                    setTimeout(() => {
                        if (cancelled) return;
                        runCinematic();
                    }, 63000);
                };

                // Start after a brief pause
                setTimeout(() => runCinematic(), 1500);

                // Store cancel fn for cleanup
                (m as any)._cinematicCancel = () => { cancelled = true; };
            }

            // Simple animate zoom if enabled (non-cinematic)
            if (params.animate && !params.cinematic && !hasAnimated.current && map.current) {
                hasAnimated.current = true;

                // Smooth zoom animation from startZoom to endZoom
                setTimeout(() => {
                    map.current?.easeTo({
                        zoom: params.endZoom,
                        duration: 3000,
                        easing: (t) => {
                            // Ease out cubic for smooth deceleration
                            return 1 - Math.pow(1 - t, 3);
                        }
                    });
                }, 500);
            }
        });

        return () => {
            clearLoadFallback();
            detachCatalogIconsRef.current?.();
            detachCatalogIconsRef.current = null;
            if (map.current) {
                (map.current as any)._cinematicCancel?.();
            }
            map.current?.remove();
            map.current = null;
        };
    }, [ghanaStyleJson]);

    return (
        <div className="w-full h-full relative">
            <div ref={mapContainer} className="w-full h-full" />

            {/* Loading splash */}
            {!isLoaded && <MapSplashLoader variant="embed" />}

            {/* Pulsing location dot (cinematic mode) — matches public map @keyframes style */}
            {isLoaded && showPulse.current && (
                <>
                    <style>{`
                        .embed-pulse-marker { position: absolute; top: 50%; left: 50%; width: 48px; height: 48px; transform: translate(-50%, -50%); pointer-events: none; z-index: 10; }
                        .embed-pulse-ring { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 60px; height: 60px; background: rgba(34, 211, 238, 0.35); border: 2px solid rgba(34, 211, 238, 0.5); border-radius: 50%; animation: embed-pulse-ring 1.5s ease-out infinite; }
                        .embed-pulse-ring-2 { animation-delay: 0.75s; }
                        .embed-pulse-dot { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 16px; height: 16px; background: linear-gradient(135deg, #22d3ee 0%, #06b6d4 100%); border: 3px solid white; border-radius: 50%; box-shadow: 0 3px 12px rgba(34, 211, 238, 0.5), 0 0 0 2px rgba(34, 211, 238, 0.2); z-index: 3; animation: embed-dot-pulse 1.5s ease-in-out infinite; }
                        @keyframes embed-pulse-ring { 0% { transform: translate(-50%, -50%) scale(0.3); opacity: 1; } 50% { opacity: 0.6; } 100% { transform: translate(-50%, -50%) scale(2.5); opacity: 0; } }
                        @keyframes embed-dot-pulse { 0%, 100% { transform: translate(-50%, -50%) scale(1); box-shadow: 0 3px 12px rgba(34, 211, 238, 0.5), 0 0 0 2px rgba(34, 211, 238, 0.2); } 50% { transform: translate(-50%, -50%) scale(1.1); box-shadow: 0 4px 16px rgba(34, 211, 238, 0.7), 0 0 0 4px rgba(34, 211, 238, 0.3); } }
                    `}</style>
                    <div className="embed-pulse-marker">
                        <div className="embed-pulse-ring" />
                        <div className="embed-pulse-ring embed-pulse-ring-2" />
                        <div className="embed-pulse-dot" />
                    </div>
                </>
            )}

            {/* Coordal branding — top left */}
            <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-1.5 flex items-center gap-2 z-10">
                <svg className="w-5 h-5" viewBox="0 0 512 512" fill="none">
                    <ellipse cx="256" cy="256" rx="102" ry="218" stroke="#10b981" strokeWidth="19" fill="none" opacity="0.7" />
                    <ellipse cx="256" cy="256" rx="218" ry="102" stroke="#10b981" strokeWidth="19" fill="none" opacity="0.7" />
                    <line x1="38" y1="256" x2="474" y2="256" stroke="#10b981" strokeWidth="26" opacity="0.35" />
                    <line x1="256" y1="38" x2="256" y2="474" stroke="#10b981" strokeWidth="26" opacity="0.35" />
                    <circle cx="333" cy="179" r="64" fill="#10b981" />
                    <circle cx="333" cy="179" r="26" fill="white" />
                </svg>
                <span className="text-white text-xs font-medium">Coordal Maps</span>
            </div>

            {/* Compact attribution — info icon, expands on click */}
            <div className="absolute bottom-1 left-1 z-10">
                {showAttribution ? (
                    <div className="bg-black/50 backdrop-blur-sm rounded px-2 py-1 flex items-center gap-1.5 text-[10px] text-white/70">
                        <button onClick={() => setShowAttribution(false)} className="hover:text-white/90 mr-0.5" title="Close">✕</button>
                        <span>© Coordal Maps</span>
                        <span className="opacity-40">|</span>
                        <span>© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer" className="hover:text-white/90 underline">OpenStreetMap</a></span>
                        <span className="opacity-40">|</span>
                        <a href={window.location.origin} target="_blank" rel="noopener noreferrer" className="hover:text-white/90 underline">Improve this map</a>
                    </div>
                ) : (
                    <button onClick={() => setShowAttribution(true)} className="bg-black/50 backdrop-blur-sm rounded-full w-5 h-5 flex items-center justify-center text-white/60 hover:text-white/90 text-[10px]" title="Map attribution">
                        ⓘ
                    </button>
                )}
            </div>
        </div>
    );
}
